import { useState } from "react";
import { motion } from "framer-motion";
import { Linkedin, Mail, ArrowUp, Users, Loader2, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const footerLinks = [
  { name: "About", href: "#about" },
  { name: "Book", href: "#book" },
  { name: "Community", href: "#community" },
  { name: "Speaking", href: "#speaking" },
  { name: "Contact", href: "#contact" },
];

const socialLinks = [
  { 
    name: "LinkedIn", 
    icon: Linkedin, 
    href: "https://www.linkedin.com/in/shearichburg/" 
  },
  { 
    name: "Pivot To Power Network", 
    icon: Users, 
    href: "https://pivot2powernetwork.com/" 
  },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/subscribe`, {
        name: "Newsletter Subscriber",
        email,
        source: "newsletter",
      });
      
      if (response.data.status === "success" || response.data.status === "already_subscribed") {
        setIsSubscribed(true);
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <footer
      className="border-t border-primary/20 bg-card/50 relative"
      data-testid="footer"
      id="newsletter"
    >
      {/* Top glow line */}
      <div className="absolute top-0 left-0 right-0 h-px glow-line" />
      
      {/* Main Footer */}
      <div className="py-16 md:py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Left Column - CTA */}
            <div className="lg:col-span-7">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="font-['Orbitron'] text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-foreground mb-6"
              >
                Let's Talk
                <span className="gradient-text">.</span>
              </motion.h2>
              <p className="text-base sm:text-lg text-muted-foreground mb-8 max-w-xl">
                Join thousands of professionals getting weekly insights on AI, entrepreneurship, 
                and the future of work delivered straight to their inbox.
              </p>

              {/* Newsletter Form */}
              {!isSubscribed ? (
                <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md">
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="bg-background border-primary/30 focus:border-primary rounded-full h-12 px-6 flex-1 text-foreground placeholder:text-muted-foreground"
                    data-testid="footer-newsletter-email"
                  />
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="bg-primary text-primary-foreground rounded-full h-12 px-8 font-bold uppercase tracking-wider hover:bg-primary/90 transition-all btn-glow"
                    data-testid="footer-newsletter-submit"
                  >
                    {isLoading ? (
                      <Loader2 className="animate-spin" size={20} />
                    ) : (
                      "Subscribe"
                    )}
                  </Button>
                </form>
              ) : (
                <div className="flex items-center gap-3 text-primary">
                  <CheckCircle size={20} />
                  <span className="font-bold uppercase tracking-wider">You're subscribed!</span>
                </div>
              )}
            </div>

            {/* Right Column - Links */}
            <div className="lg:col-span-5">
              <div className="grid grid-cols-2 gap-8">
                {/* Navigation */}
                <div>
                  <h4 className="text-sm font-['Orbitron'] font-bold uppercase tracking-wider text-foreground mb-4">Navigate</h4>
                  <nav className="flex flex-col gap-3">
                    {footerLinks.map((link) => (
                      <button
                        key={link.name}
                        onClick={() => scrollToSection(link.href)}
                        className="text-sm uppercase tracking-wider text-muted-foreground hover:text-primary transition-colors text-left"
                        data-testid={`footer-link-${link.name.toLowerCase()}`}
                      >
                        {link.name}
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Social */}
                <div>
                  <h4 className="text-sm font-['Orbitron'] font-bold uppercase tracking-wider text-foreground mb-4">Connect</h4>
                  <div className="flex flex-col gap-3">
                    {socialLinks.map((link) => (
                      <a
                        key={link.name}
                        href={link.href}
                        target={link.href.startsWith("http") ? "_blank" : undefined}
                        rel={link.href.startsWith("http") ? "noopener noreferrer" : undefined}
                        className="flex items-center gap-2 text-sm uppercase tracking-wider text-muted-foreground hover:text-primary transition-colors"
                        data-testid={`footer-social-${link.name.toLowerCase().replace(/\s/g, "-")}`}
                      >
                        <link.icon size={16} />
                        {link.name}
                      </a>
                    ))}
                    <a
                      href="mailto:srichburg@futurewisegroup.com"
                      className="flex items-center gap-2 text-sm uppercase tracking-wider text-muted-foreground hover:text-primary transition-colors"
                      data-testid="footer-email-link"
                    >
                      <Mail size={16} />
                      Email
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-primary/20 py-6 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 text-sm text-muted-foreground">
            <span>© {new Date().getFullYear()} <span className="text-primary">Shea Richburg</span>. All rights reserved.</span>
          </div>
          
          {/* Back to Top */}
          <button
            onClick={scrollToTop}
            className="flex items-center gap-2 text-sm uppercase tracking-wider text-muted-foreground hover:text-primary transition-colors"
            data-testid="footer-back-to-top"
          >
            Back to top
            <ArrowUp size={16} />
          </button>
        </div>
      </div>
    </footer>
  );
}
